/* ns-motion-journey — purchase-journey micro-interactions (site-wide).
 *
 * This file is an OBSERVER of the cart, never a participant. It adds no fetch,
 * intercepts no click, calls no preventDefault and wraps nothing. Every hook
 * below is a passive listener on an event the theme already dispatches:
 *
 *   variant:add   assets/ns-cart-add.js, assets/ns-cart-drawer-v2.js and the
 *                 theme's own ProductForm all dispatch this once a line is in
 *                 the cart. It is the success signal.
 *   cart:change   dispatched alongside it with the fresh cart, which is where
 *                 the new item_count comes from.
 *   toggle        the theme's DialogElement fires it on <cart-drawer> the moment
 *                 the `open` attribute flips, which is earlier than
 *                 dialog:after-show and so runs WITH the panel rather than after.
 *
 * An add to cart therefore cannot be delayed, doubled or blocked by anything in
 * here. The one thing this file writes is class names, and every visual state
 * they drive is defined in assets/ns-motion-journey.css under `.ns-motion-ok`.
 */
(function () {
  if (window.NSJourney) return;

  var reduced =
    (window.NSMotion && window.NSMotion.reduced) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Buttons that share the press language. Kept as one list so the drawer, the
     PDP and the sticky bar cannot drift apart. `buy-buttons > .button` is a
     DIRECT child on purpose — the dynamic checkout button is also inside
     <buy-buttons>, and that one belongs to Shopify. */
  var BTN =
    'buy-buttons > .button, .shopify-product-form button[type="submit"],' +
    '.ns-cdv2__card-add, .ns-cdv2__checkout, .pdp-sticky-atc-section__cta';

  /* Only a real product-form submit gets the tick-and-"Added" layer. The
     drawer's upsell button already has an ADDED state of its own in
     assets/ns-cart-drawer-v2.css, and the checkout button navigates away, so
     neither would ever show it. */
  var OK_BTN = 'buy-buttons > .button, .shopify-product-form button[type="submit"]';

  /* Shopify owns the dynamic checkout button's DOM and re-renders it from its
     own script. Nothing is added to it, or inside it, ever. */
  function theirs(el) {
    return !!(el.closest('.shopify-payment-button') || el.closest('shopify-accelerated-checkout'));
  }

  var CHECK =
    '<svg class="ns-j-ok__check" width="18" height="18" viewBox="0 0 18 18" fill="none"' +
    ' aria-hidden="true" focusable="false"><path d="M3.5 9.3 7 12.8 14.5 5.3"' +
    ' stroke="currentColor" stroke-width="2" stroke-linecap="round"' +
    ' stroke-linejoin="round"/></svg>';

  function each(scope, selector, fn) {
    (scope || document).querySelectorAll(selector).forEach(fn);
  }

  /* The single source of truth for "is motion wanted here". `ns-motion-ok` is
     set early in <head> by layout/theme.liquid and taken off again by
     assets/ns-motion-core.js if the visitor asked for reduced motion or the
     observer is unavailable, so it answers for both cases at once. */
  function motionOn() {
    return document.documentElement.classList.contains('ns-motion-ok');
  }

  /* One-shot class: add it, then take it off once its animation window closes,
     so a repeat press replays the moment instead of being swallowed. */
  function flash(el, cls, ms) {
    if (!el) return;
    el.classList.remove(cls);
    // Reading offsetWidth forces the removal to land before the re-add, which
    // is what restarts a CSS animation on a second press.
    void el.offsetWidth;
    el.classList.add(cls);
    clearTimeout(el['__t' + cls]);
    el['__t' + cls] = setTimeout(function () {
      el.classList.remove(cls);
    }, ms);
  }

  function adopt(scope) {
    each(scope, BTN, function (btn) {
      if (theirs(btn)) return;
      btn.classList.add('ns-j-btn');
    });
    // The success layer is REAL DOM inside a live buy button, so it is only
    // ever injected where it can also be styled. With motion off it would
    // otherwise render as an unstyled "✓ Added" sitting next to the label.
    if (!motionOn()) return;
    each(scope, OK_BTN, function (btn) {
      if (btn.__nsJourneyOk || theirs(btn)) return;
      btn.__nsJourneyOk = true;
      var ok = document.createElement('span');
      ok.className = 'ns-j-ok';
      ok.setAttribute('aria-hidden', 'true');
      ok.innerHTML = CHECK + '<span>Added</span>';
      btn.appendChild(ok);
    });
  }

  /* The drawer and the cart page both replace their children wholesale, so the
     buttons inside them are new elements each time and have to be adopted
     again. One rAF-coalesced pass per burst of mutations, never one per
     mutation record. */
  var queued = false;

  /*
   * Only ELEMENT insertions can bring a new button with them, and the
   * announcement bar's countdown rewrites a text node every second — which used
   * to schedule a full-document selector sweep once per second, for the whole
   * time anyone had the page open. Filtering on added element nodes takes that
   * sweep off the per-second path without changing what gets adopted.
   */
  function adoptSoon(records) {
    if (queued) return;
    var relevant = !records;
    for (var i = 0; !relevant && i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (added[j].nodeType === 1) {
          relevant = true;
          break;
        }
      }
    }
    if (!relevant) return;

    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      adopt(document);
    });
  }

  /* ── where the add is acknowledged ─────────────────────────────────────────
   * The header cart icon used to carry three of this pass's micro-interactions:
   * a bump, the 0 → 1 count ring, and a thumbnail that flew to it on an arc.
   * Measurement killed the flight and demoted the other two. This theme's
   * <header> is `position: relative` on every template — nothing in the ancestor
   * chain above .ns-sh__cart is sticky — so by the time anyone presses ADD TO
   * CART the icon has scrolled off: it sat 608px above the viewport at the exact
   * moment it bumped. The flight was worse still, because it also skipped itself
   * whenever the drawer was opening, which on this store is every PDP add; zero
   * clones were created across a measured add.
   *
   * So the flight is gone, the icon bump and badge ring are kept as the cheap
   * case for when the icon IS in view (the cart page, a shopper at the top of a
   * collection), and the acknowledgement the shopper actually sees now happens
   * inside the drawer: its own "Cart (n)" title takes the same celebration, and
   * the just-added row is highlighted underneath it.
   */
  function cartIcon() {
    return document.querySelector('.ns-sh__cart, a[aria-controls="cart-drawer"]');
  }

  /*
   * The drawer's title is re-rendered when the cart comes back, so a class
   * written on the node that is on screen now can be thrown away moments later.
   * The same short poll that markAdded() uses: flash whichever title element is
   * current, and flash it again only if the element has been REPLACED. Two
   * identical pops on two different nodes read as one, because the first node is
   * removed from the document as the second arrives.
   */
  function celebrateTitle(first) {
    var cls = first ? 'ns-j-count-first' : 'ns-j-count-pop';
    var hold = first ? 900 : 700;
    var done = null;
    var startedAt = 0;
    var tries = 0;

    (function look() {
      var title = document.querySelector('cart-drawer .ns-cdv2__title');
      var now = Date.now();
      // Re-mark a replacement only while the ORIGINAL pop would still have been
      // playing. Past that the moment has been seen and a second one would read
      // as a stutter rather than a continuation.
      if (title && title !== done && (!startedAt || now - startedAt < hold)) {
        if (!startedAt) startedAt = now;
        done = title;
        flash(title, cls, hold);
      }
      if (startedAt && Date.now() - startedAt >= hold) return;
      if (++tries < 10) setTimeout(look, 120);
    })();
  }

  /* ── success on the button that was pressed ───────────────────────────────
   * `variant:add` bubbles from the form for a product-form add, so the pressed
   * button is found from the event path. An add from somewhere else (the drawer
   * upsell dispatches on documentElement) has no button here, and correctly
   * gets no tick.
   */
  var lastPressed = null;

  document.addEventListener(
    'pointerdown',
    function (event) {
      var btn = event.target.closest && event.target.closest(BTN);
      if (btn) lastPressed = btn;
    },
    { passive: true }
  );

  function successButton(event) {
    var form = event.target && event.target.tagName === 'FORM' ? event.target : null;
    var btn = form && form.querySelector(OK_BTN);
    if (btn) return btn;
    if (lastPressed && lastPressed.matches(OK_BTN) && lastPressed.isConnected) return lastPressed;
    return null;
  }

  /* ── cart count ───────────────────────────────────────────────────────────
   * The theme's <cart-count> rolls its digits for n → m but only fades for
   * 0 → 1, so the badge's first appearance is the case worth marking.
   */
  function readCount() {
    var el = document.querySelector('cart-count');
    var n = el ? parseInt(el.innerText, 10) : 0;
    return isNaN(n) ? 0 : n;
  }

  var count = readCount();

  /* The drawer entrance currently in flight, if any: { drawer, at, settle }.
     Module-level because both watchDrawer() and onCart() close it. */
  var entrance = null;

  function onCart(event) {
    /*
     * A cart change that lands AFTER the panel has opened re-renders its
     * children, and a re-inserted element restarts its CSS animation — so the
     * entrance would play a second time underneath the shopper. Closing the
     * window here means the re-render simply repaints. The 150ms skips the
     * change that opened the drawer in the first place, which arrives in the
     * same tick as the `toggle`.
     */
    if (entrance && Date.now() - entrance.at > 150) endEntrance();

    var cart = event.detail && event.detail.cart;
    if (!cart) return;
    var next = typeof cart.item_count === 'number' ? cart.item_count : count;
    var wasEmpty = count === 0;
    count = next;

    var first = wasEmpty && next > 0;
    each(document, 'cart-count', function (el) {
      flash(el, first ? 'ns-j-count-first' : 'ns-j-count-pop', first ? 780 : 500);
    });
    // The same moment, on the surface that is actually in front of the shopper.
    celebrateTitle(first);

    progress();
  }

  /* ── reward bar ──────────────────────────────────────────────────────────
   * The drawer is re-rendered wholesale on a cart change, so the pill arrives
   * already at its new width with no previous value to ease from. The last
   * percentage seen is handed back as `--ns-j-from`, which is what turns an
   * instant jump into a fill.
   */
  var lastProgress = null;

  function progress() {
    // The markup is swapped after cart:change, so the new pill is read on the
    // next frame rather than the current one.
    requestAnimationFrame(function () {
      each(document, '.ns-cdv2__promo', function (promo) {
        var now = promo.style.getPropertyValue('--cdv2-promo-progress').trim();
        var unlocked = promo.classList.contains('ns-cdv2__promo--unlocked');

        if (!unlocked && now && lastProgress !== null && now !== lastProgress) {
          promo.style.setProperty('--ns-j-from', lastProgress);
          flash(promo, 'ns-j-progress', 1100);
        }
        if (unlocked && lastProgress !== null && lastProgress !== 'done') {
          flash(promo, 'ns-j-unlocked', 1100);
        }
        lastProgress = unlocked ? 'done' : now || '0%';
      });
    });
  }

  /* ── the add itself ─────────────────────────────────────────────────────── */
  document.addEventListener('cart:change', onCart);

  /* The row that was just added, marked so the eye lands on it. The drawer's
     markup can arrive either side of `variant:add` depending on which surface
     did the add — ns-cart-add.js dispatches variant:add first, the drawer's own
     upsell dispatches cart:change first — so the row is looked for over a short
     window rather than on one frame. */
  var marking = null;

  function markAdded(key) {
    if (!key) return;
    marking = key;
    var selector =
      '[data-line-key="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]';
    var flashed = null;
    var startedAt = 0;
    var tries = 0;
    // The highlight's own length. Past it, the moment has been seen.
    var HOLD = 1100;

    /*
     * Polled rather than done once, for two reasons that both showed up in
     * testing. The drawer's markup can arrive AFTER `variant:add` (which
     * surface dispatched the add decides the order), and CartDrawer replaces
     * its children wholesale when the cart comes back — so a class written on
     * the first matching row can be thrown away with that row moments later.
     * Re-marking whenever the matching row is a DIFFERENT element covers both.
     *
     * The re-mark is time-boxed to the highlight's own duration. The free-gift
     * pass re-renders the panel about 1.5s after the add, and without the box
     * that swap started a SECOND highlight on a row the shopper had already
     * watched light up once — one moment, played twice, the second one late.
     */
    (function look() {
      if (marking !== key) return;
      var cell = document.querySelector(selector);
      var row = cell && cell.closest('.ns-cdv2__item');
      var now = Date.now();
      if (row && row !== flashed && (!startedAt || now - startedAt < HOLD)) {
        if (!startedAt) startedAt = now;
        flashed = row;
        flash(row, 'ns-j-added', 1200);
      }
      if (startedAt && Date.now() - startedAt >= HOLD) {
        marking = null;
        return;
      }
      if (++tries < 14) setTimeout(look, 120);
      else marking = null;
    })();
  }

  document.addEventListener('variant:add', function (event) {
    var btn = successButton(event);
    if (btn) flash(btn, 'is-ok', 1600);

    var items = (event.detail && event.detail.items) || [];
    markAdded(items.length && items[0] ? items[0].key : null);

    // Two class writes, ungated: on the surfaces where the icon IS in view it is
    // the whole acknowledgement, and where it is not it costs nothing.
    var icon = cartIcon();
    if (icon) flash(icon, 'ns-j-cart-bump', 560);
  });

  /* ── drawer ───────────────────────────────────────────────────────────────
   * `ns-j-open` is what gates every entrance in the stylesheet. It is removed
   * once the entrance window closes so that a later re-render — a quantity
   * change, an upsell add — repaints the panel without replaying the whole
   * staggered arrival underneath the shopper's finger.
   */
  function endEntrance() {
    if (!entrance) return;
    clearTimeout(entrance.settle);
    entrance.drawer.classList.remove('ns-j-open');
    entrance = null;
  }

  function watchDrawer(drawer) {
    if (drawer.__nsJourney) return;
    drawer.__nsJourney = true;

    /*
     * The class comes off when the entrance has actually FINISHED, not on a
     * fixed timer. It used to be removed at a flat 1700ms, which landed in the
     * middle of the last block's animation and produced a hard snap. Every
     * ns-j-rise / ns-j-wipe that ends pushes the removal 80ms further out, so the
     * last one to finish is the one that closes the window. These animations run
     * on the drawer's LIGHT DOM children, so they bubble here.
     */
    drawer.addEventListener('animationend', function (event) {
      if (!entrance || entrance.drawer !== drawer) return;
      if (event.animationName !== 'ns-j-rise' && event.animationName !== 'ns-j-wipe') return;
      clearTimeout(entrance.settle);
      entrance.settle = setTimeout(endEntrance, 80);
    });

    drawer.addEventListener('toggle', function () {
      if (!drawer.hasAttribute('open')) {
        drawer.__nsJEntered = false;
        endEntrance();
        drawer.classList.remove('ns-j-open');
        return;
      }
      /*
       * ONE entrance per open. A CSS animation restarts whenever its element is
       * re-inserted into the document, and this drawer re-renders itself inside
       * the open window more than once — the free-gift pass comes back about
       * 250ms after the first paint, and any quantity change re-renders it too.
       * Without this flag, and without onCart() closing the window below, the
       * shopper watched their cart arrive, leave and arrive again.
       */
      if (drawer.__nsJEntered) return;
      drawer.__nsJEntered = true;
      drawer.classList.add('ns-j-open');
      entrance = {
        drawer: drawer,
        at: Date.now(),
        // Backstop only, for an open with nothing to animate (an empty cart)
        // where no animationend ever arrives.
        settle: setTimeout(endEntrance, 1200),
      };
    });
  }

  /* Removal and quantity both answer the tap before the server does. Neither
     listener touches the event: the theme's own handler in
     assets/ns-cart-drawer-v2.js runs unchanged. */
  /*
   * A removal ends in a STATIC state, not in a finished animation.
   *
   * The row used to keep `ns-j-leaving` for the whole 2500ms time-box, and the
   * theme re-inserts that same node when the cart re-renders — which restarts a
   * CSS animation. Measured: the row leaned out by 440ms, jumped back to full
   * opacity at 1075ms and leaned out a second time. The tap was answered, then
   * un-answered, then answered again. Swapping to `ns-j-left` on `animationend`
   * fixes it at the source: a static end state has nothing to restart.
   *
   * The time-box survives as what it always was — the failure backstop. A remove
   * that errors re-reads the cart instead of swapping the panel, and on the path
   * where that re-read also fails there would be no swap at all, so BOTH classes
   * are cleared after 2500ms and a real cart line comes back rather than being
   * stranded invisible.
   */
  function leaveRow(row) {
    row.classList.remove('ns-j-left');
    flash(row, 'ns-j-leaving', 2500);

    clearTimeout(row.__nsJLeft);
    row.__nsJLeft = setTimeout(function () {
      row.classList.remove('ns-j-left');
    }, 2500);

    row.addEventListener('animationend', function onEnd(event) {
      if (event.animationName !== 'ns-j-leave') return;
      row.removeEventListener('animationend', onEnd);
      row.classList.remove('ns-j-leaving');
      row.classList.add('ns-j-left');
    });
  }

  document.addEventListener(
    'click',
    function (event) {
      var remove = event.target.closest && event.target.closest('.ns-cdv2__remove');
      if (!remove) return;
      var row = remove.closest('.ns-cdv2__item');
      if (row) leaveRow(row);
    },
    { passive: true }
  );

  document.addEventListener('change', function (event) {
    var select = event.target.closest && event.target.closest('.ns-cdv2__qty-select');
    if (!select) return;
    flash(select.closest('.ns-cdv2__qty'), 'ns-j-qty', 520);
    settleKey = select.getAttribute('data-line-key');
  });

  /* The line's thumbnail steps back while its quantity request is in flight
     (see the loading state in the CSS). The cart re-render swaps in a fresh,
     full-size thumbnail, so the changed line is found again by its line key
     and eased back up from the stepped-back state. */
  var settleKey = null;

  document.addEventListener('cart:change', function () {
    if (!settleKey || !motionOn()) return;
    var key = settleKey;
    settleKey = null;
    requestAnimationFrame(function () {
      each(document, '.ns-cdv2__qty-select', function (select) {
        if (select.getAttribute('data-line-key') !== key) return;
        var item = select.closest('.ns-cdv2__item');
        flash(item && item.querySelector('.ns-cdv2__item-img'), 'ns-j-settle', 500);
      });
    });
  });

  /* ── sticky add-to-cart bar ───────────────────────────────────────────────
   * The section toggles `.show` on the bar from its own scroll handler. This
   * only watches for that class, so no second scroll listener is added, and
   * nudges the CTA once per session the first time the bar arrives.
   */
  var NUDGED = 'nsJourneyStickyNudged';

  function watchSticky(bar) {
    if (bar.__nsJourney || typeof MutationObserver === 'undefined') return;
    bar.__nsJourney = true;

    var seen = false;
    try {
      seen = window.sessionStorage.getItem(NUDGED) === '1';
    } catch (error) {
      // Private mode can throw on read. A nudge that shows twice in a session
      // is a smaller problem than a thrown error, so this keeps going.
      seen = false;
    }

    new MutationObserver(function () {
      if (seen || reduced || !bar.classList.contains('show')) return;
      seen = true;
      try {
        window.sessionStorage.setItem(NUDGED, '1');
      } catch (error) {
        /* see above */
      }
      var cta = bar.querySelector('.pdp-sticky-atc-section__cta');
      if (cta) setTimeout(function () { flash(cta, 'ns-j-nudge', 1100); }, 420);
    }).observe(bar, { attributes: true, attributeFilter: ['class'] });
  }

  /* ── announcement bar ─────────────────────────────────────────────────────
   * The bar's own script owns the timer and publishes `pause`/`resume`
   * (sections/announcement-bar.liquid). Holding the rotation while a shopper is
   * reading it — hovering, mid-touch, or with the tab in the background — is
   * what keeps the message change feeling considered rather than snatched away.
   */
  function hold(on) {
    var api = window.nsAnnouncementRotate;
    if (api) api[on ? 'pause' : 'resume']();
  }

  /* Registered ONCE, at module scope. It used to be registered inside
     watchAnnouncement, whose only re-entry guard is a property on the bar
     element — so every `shopify:section:load` that replaced the announcement bar
     (routine in the theme editor) left the previous listener attached forever and
     added another. It needs no reference to the bar: the API it calls is global. */
  document.addEventListener('visibilitychange', function () {
    hold(document.hidden);
  });

  function watchAnnouncement(bar) {
    if (bar.__nsJourney) return;
    bar.__nsJourney = true;

    /* Pointer and keyboard only. `touchstart`/`touchend` were here too, and a
       touch that lands on the bar is almost always the start of a scroll rather
       than someone settling in to read — so the rotation was being paused and
       resumed by gestures that had nothing to do with the message. */
    bar.addEventListener('mouseenter', function () { hold(true); }, { passive: true });
    bar.addEventListener('focusin', function () { hold(true); }, { passive: true });
    bar.addEventListener('mouseleave', function () { hold(false); }, { passive: true });
    bar.addEventListener('focusout', function () { hold(false); }, { passive: true });

    watchCountdown(bar);
  }

  /* ── countdown digits ─────────────────────────────────────────────────────
   * The rotation work above only runs when two or more announcements are inside
   * their live time window, and today the store has one — so the eased track and
   * the cross-dissolve are dormant, while the DAY/HRS/MINS readout is the one
   * thing in this bar that changes on every page view for every visitor. It was
   * swapping its digits instantly.
   *
   * Each segment is watched for a REAL change in value. The section's own tick
   * rewrites all three slots every second whether or not they differ, so the
   * mutation alone is not the signal — the previous string is remembered and
   * compared, or HRS would roll sixty times too often.
   */
  function watchCountdown(bar) {
    if (typeof MutationObserver === 'undefined' || !motionOn()) return;

    each(bar, '.time-value', function (seg) {
      if (seg.__nsJourney) return;
      seg.__nsJourney = true;

      // The digit has to move INSIDE the segment for the segment to clip it, so
      // the moving element is the inner span that fa-announcement.liquid renders.
      // Falling back to the segment itself keeps a plain rise-and-fade on any
      // markup that has no span rather than losing the motion entirely.
      var digit = seg.querySelector('span') || seg;
      var last = seg.textContent.trim();

      new MutationObserver(function () {
        var now = seg.textContent.trim();
        if (now === last) return;
        last = now;
        flash(digit, 'ns-j-tick', 420);
      }).observe(seg, { childList: true, characterData: true, subtree: true });
    });
  }

  /* ── wiring ───────────────────────────────────────────────────────────────
   * Re-run on section load so the theme editor, and any section the cart
   * re-renders, get the same treatment as the first paint.
   */
  function scan(scope) {
    adopt(scope);
    each(scope, 'cart-drawer', watchDrawer);
    each(scope, '#sticky-atc-bar', watchSticky);
    each(scope, '.sec-announcement-bar', watchAnnouncement);
  }

  function start() {
    scan(document);
    count = readCount();
    // Seed the reward bar's previous value from the server-rendered panel, so
    // the FIRST add already fills rather than jumping.
    each(document, '.ns-cdv2__promo', function (promo) {
      lastProgress = promo.classList.contains('ns-cdv2__promo--unlocked')
        ? 'done'
        : promo.style.getPropertyValue('--cdv2-promo-progress').trim() || '0%';
    });
    document.addEventListener('shopify:section:load', function (event) {
      scan(event.target);
    });
    if (typeof MutationObserver !== 'undefined' && document.body) {
      new MutationObserver(adoptSoon).observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  window.NSJourney = { scan: scan };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
